import React from 'react';
import { QuestionDefinition } from '../../../types/survey.types';

interface AdvancedSettingsProps {
  question: QuestionDefinition;
  onChange: (question: QuestionDefinition) => void;
}

const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({ question, onChange }) => {
  const updateSettings = (newSettings: any) => {
    onChange({
      ...question,
      settings: { ...question.settings, ...newSettings },
    });
  };

  // Text/Email specific settings
  const renderTextSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Max Characters
        </label>
        <input
          type="number"
          min="1"
          max="10000"
          value={question.settings?.maxLength || 500}
          onChange={(e) => updateSettings({ maxLength: parseInt(e.target.value) || 500 })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Maximum number of characters allowed (default: 500)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          maxLength={500}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Provide additional guidance or clarification..."
        />
        <p className="mt-1.5 text-xs text-gray-500">
          This text will appear below the question to help respondents understand what's expected.
        </p>
      </div>
    </div>
  );

  // Textarea specific settings
  const renderTextAreaSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Number of Rows
        </label>
        <input
          type="number"
          min="2"
          max="20"
          value={question.settings?.rows || 4}
          onChange={(e) => updateSettings({ rows: parseInt(e.target.value) || 4 })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Height of the textarea (2-20 rows, default: 4)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Max Characters
        </label>
        <input
          type="number"
          min="1"
          max="10000"
          value={question.settings?.maxLength || 2000}
          onChange={(e) => updateSettings({ maxLength: parseInt(e.target.value) || 2000 })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Maximum number of characters allowed (default: 2000)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          maxLength={500}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Provide additional guidance or clarification..."
        />
        <p className="mt-1.5 text-xs text-gray-500">
          This text will appear below the question to help respondents understand what's expected.
        </p>
      </div>
    </div>
  );

  // Number specific settings
  const renderNumberSettings = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Minimum Value
          </label>
          <input
            type="number"
            value={question.settings?.minValue ?? 0}
            onChange={(e) => updateSettings({ minValue: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Value
          </label>
          <input
            type="number"
            value={question.settings?.maxValue ?? 100}
            onChange={(e) => updateSettings({ maxValue: parseFloat(e.target.value) || 100 })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Step Size
        </label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          value={question.settings?.step || 1}
          onChange={(e) => updateSettings({ step: parseFloat(e.target.value) || 1 })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Increment/decrement value (default: 1)
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Radio/Checkbox/Dropdown settings
  const renderChoiceSettings = () => (
    <div className="space-y-5">
      {(question.type === 'radio' || question.type === 'checkbox') && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Layout
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="layout"
                value="vertical"
                checked={question.settings?.layout === 'vertical' || !question.settings?.layout}
                onChange={(e) => updateSettings({ layout: e.target.value })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Vertical</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="layout"
                value="horizontal"
                checked={question.settings?.layout === 'horizontal'}
                onChange={(e) => updateSettings({ layout: e.target.value })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Horizontal</span>
            </label>
          </div>
          <p className="mt-1.5 text-xs text-gray-500">
            How the options should be displayed
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Rating/Scale settings
  const renderRatingScaleSettings = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Minimum Value
          </label>
          <input
            type="number"
            min="1"
            value={question.settings?.minValue || 1}
            onChange={(e) => updateSettings({ minValue: parseInt(e.target.value) || 1 })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Value
          </label>
          <input
            type="number"
            min="2"
            max="10"
            value={question.settings?.maxValue || 5}
            onChange={(e) => updateSettings({ maxValue: parseInt(e.target.value) || 5 })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Scale Labels <span className="text-gray-400 font-normal">(one per scale point)</span>
        </label>
        <div className="space-y-2">
          {Array.from({ length: (question.settings?.maxValue || 5) - (question.settings?.minValue || 1) + 1 }, (_, i) => {
            const value = (question.settings?.minValue || 1) + i;
            const labels = question.settings?.scaleLabels || [];
            return (
              <div key={i} className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700 w-8">{value}:</span>
                <input
                  type="text"
                  value={labels[i] || ''}
                  onChange={(e) => {
                    const newLabels = [...(question.settings?.scaleLabels || [])];
                    newLabels[i] = e.target.value;
                    updateSettings({ scaleLabels: newLabels });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={`Label for ${value}`}
                />
              </div>
            );
          })}
        </div>
        <p className="mt-1.5 text-xs text-gray-500">
          Optional labels to describe each scale point
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Date settings
  const renderDateSettings = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Minimum Date <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="date"
            value={question.settings?.minDate || ''}
            onChange={(e) => updateSettings({ minDate: e.target.value })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Date <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="date"
            value={question.settings?.maxDate || ''}
            onChange={(e) => updateSettings({ maxDate: e.target.value })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Yes/No settings
  const renderYesNoSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Dosing Schedule settings
  const renderDosingScheduleSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={question.settings?.includeDoseAmount !== false}
            onChange={(e) => updateSettings({ includeDoseAmount: e.target.checked })}
            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
          />
          <div>
            <span className="text-sm font-semibold text-gray-700">Include Dose Amount Field</span>
            <p className="text-xs text-gray-500">Allow respondents to enter their dose per administration</p>
          </div>
        </label>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Available Dose Units
        </label>
        <input
          type="text"
          value={(question.settings?.availableDoseUnits || ['mL', 'IU', 'mcg']).join(', ')}
          onChange={(e) => updateSettings({
            availableDoseUnits: e.target.value.split(',').map(u => u.trim()).filter(Boolean),
          })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          placeholder="mL, IU, mcg"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Comma-separated list of units (default: mL, IU, mcg)
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Max Cycle Duration
          </label>
          <input
            type="number"
            min="1"
            max="104"
            value={question.settings?.maxCycleDuration || 52}
            onChange={(e) => updateSettings({ maxCycleDuration: parseInt(e.target.value) || 52 })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          />
          <p className="mt-1.5 text-xs text-gray-500">Max duration value allowed</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Default Duration Unit
          </label>
          <div className="flex space-x-4 mt-1">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="cycleUnit"
                value="weeks"
                checked={question.settings?.defaultCycleUnit !== 'months'}
                onChange={() => updateSettings({ defaultCycleUnit: 'weeks' })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Weeks</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="cycleUnit"
                value="months"
                checked={question.settings?.defaultCycleUnit === 'months'}
                onChange={() => updateSettings({ defaultCycleUnit: 'months' })}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Months</span>
            </label>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Measurement settings
  const renderMeasurementSettings = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Available Units
        </label>
        <input
          type="text"
          value={(question.settings?.measurementUnits || ['mg', 'mL', 'g', 'IU', 'mcg', 'units']).join(', ')}
          onChange={(e) => updateSettings({
            measurementUnits: e.target.value.split(',').map(u => u.trim()).filter(Boolean),
          })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          placeholder="mg, mL, g, IU, mcg, units"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Comma-separated list of measurement units
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Default Unit
        </label>
        <select
          value={question.settings?.defaultMeasurementUnit || 'mg'}
          onChange={(e) => updateSettings({ defaultMeasurementUnit: e.target.value })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none cursor-pointer"
        >
          {(question.settings?.measurementUnits || ['mg', 'mL', 'g', 'IU', 'mcg', 'units']).map((unit) => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Minimum Value
          </label>
          <input
            type="number"
            value={question.settings?.measurementMinValue ?? ''}
            onChange={(e) => updateSettings({ measurementMinValue: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
            placeholder="No minimum"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Maximum Value
          </label>
          <input
            type="number"
            value={question.settings?.measurementMaxValue ?? ''}
            onChange={(e) => updateSettings({ measurementMaxValue: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
            className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
            placeholder="No maximum"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Step Size
        </label>
        <input
          type="number"
          min="0.01"
          step="0.01"
          value={question.settings?.measurementStep || ''}
          onChange={(e) => updateSettings({ measurementStep: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none"
          placeholder="Any (default)"
        />
        <p className="mt-1.5 text-xs text-gray-500">
          Increment/decrement value for the number input
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Help Text <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <textarea
          value={question.settings?.helpText || ''}
          onChange={(e) => updateSettings({ helpText: e.target.value })}
          rows={2}
          className="w-full px-4 py-2.5 text-base border-2 border-gray-200 rounded-xl bg-white focus:bg-blue-50/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none resize-none"
          placeholder="Additional guidance for respondents..."
        />
      </div>
    </div>
  );

  // Render appropriate settings based on question type
  const renderSettings = () => {
    switch (question.type) {
      case 'text':
      case 'email':
        return renderTextSettings();
      case 'textarea':
        return renderTextAreaSettings();
      case 'number':
        return renderNumberSettings();
      case 'radio':
      case 'checkbox':
      case 'dropdown':
        return renderChoiceSettings();
      case 'rating':
      case 'scale':
        return renderRatingScaleSettings();
      case 'date':
        return renderDateSettings();
      case 'yesno':
        return renderYesNoSettings();
      case 'dosing_schedule':
        return renderDosingScheduleSettings();
      case 'measurement':
        return renderMeasurementSettings();
      default:
        return (
          <div className="text-center py-8 text-gray-500">
            <p>No advanced settings available for this question type.</p>
          </div>
        );
    }
  };

  return (
    <div>
      {renderSettings()}
    </div>
  );
};

export default AdvancedSettings;
